// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import { type FC } from 'react';
import { useBenchmarkSelector } from './use-benchmark-selector';
import { AppIcon } from '@shared/ui-kit/components/app-icon';
import { IconButton } from '@shared/ui-kit/components/icon-button';
import { SearchBar } from '@shared/ui-kit/components/search-bar';
import { useTranslation } from 'react-i18next';
import { type BenchmarkOption } from './use-benchmark-selector';

/** Props for the BenchmarkSelector component. */
export interface BenchmarkSelectorProps {
  /**
   * The currently selected benchmark value (ticker). Defaults to 'SPY' if not
   * provided.
   */
  value?: string;

  /**
   * Whether the selector is disabled. When true, the component cannot be interacted
   * with.
   */
  disabled?: boolean;

  /**
   * Callback function triggered when a benchmark is selected and confirmed.
   *
   * @param value - The selected benchmark ticker symbol
   */
  onChange?: (value: string) => void;

  /**
   * Optional list of saved benchmark options to display in a separate group at the
   * top of the list.
   */
  savedBenchmarks?: BenchmarkOption[];
}

/**
 * BenchmarkSelector component allows users to select a benchmark from a categorized
 * list.
 *
 * This component displays a chip with the current benchmark selection and opens a
 * dialog when clicked. The dialog contains a searchable list of benchmarks
 * organized by categories such as "Popular US Indexes", "International Indexes",
 * etc.
 *
 * @example
 *
 * ```tsx
 * <BenchmarkSelector
 *   value="SPY"
 *   onChange={newValue => console.log(`Selected: ${newValue}`)}
 * />;
 * ```
 */
export const BenchmarkSelector: FC<BenchmarkSelectorProps> = ({
  value,
  disabled,
  onChange,
  savedBenchmarks,
}) => {
  const { t } = useTranslation();

  const {
    currentBenchmarkLabel,
    open,
    onOpen,
    onRefresh,
    onClose,
    onSave,
    isFetching,
    selectedBenchmark,
    onSelectBenchmark,
    filteredBenchmarks,
    searchQuery,
    setSearchQuery,
    benchmarksQuery,
  } = useBenchmarkSelector({
    value,
    disabled,
    onChange,
    savedBenchmarks,
  });

  return (
    <div data-testid="benchmark-selector">
      <Chip
        label={t('benchmark-selector.label', {
          benchmark: currentBenchmarkLabel,
          ns: 'components',
        })}
        deleteIcon={<AppIcon icon="Pen" color="inherit" size="md" />}
        variant="outlined"
        color="primary"
        onClick={onOpen}
        onDelete={onOpen}
        disabled={disabled}
        className="disabled:!cursor-not-allowed"
        data-testid="benchmark-selector-chip"
      />

      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          className: '!max-w-[400px] !max-h-[80vh] !w-full',
        }}
        data-testid="benchmark-selector-dialog"
      >
        <DialogTitle
          component="div"
          className="flex items-center justify-center border border-border-divider-light"
        >
          <Typography variant="h5" className="!ml-auto">
            {t('benchmark-selector.title', { ns: 'components' })}
          </Typography>
          <div className="ml-auto flex items-center justify-center">
            {isFetching ? (
              <CircularProgress size={20} data-testid="loading-spinner" />
            ) : (
              <Tooltip
                title={t('benchmark-selector.refresh', { ns: 'components' })}
              >
                <IconButton
                  icon="Refresh"
                  variant="icon"
                  onClick={onRefresh}
                  size={20}
                  data-testid="refresh-button"
                />
              </Tooltip>
            )}
          </div>
        </DialogTitle>

        <DialogContent>
          <div className="py-4">
            <SearchBar
              value={searchQuery}
              onChangeValue={e => setSearchQuery(e.target.value)}
              placeholder={t('benchmark-selector.search-placeholder', {
                ns: 'components',
              })}
              autoFocus
              onKeyDown={e => {
                if (e.key !== 'Escape') {
                  e.stopPropagation();
                }
              }}
              data-testid="search-bar"
            />
          </div>
          <div className="max-h-[300px] overflow-auto">
            {filteredBenchmarks.length === 0 ? (
              <Typography
                className="w-full justify-center text-center"
                color="text.secondary"
                sx={{ py: 2 }}
                data-testid="no-benchmarks"
              >
                {t('benchmark-selector.no-benchmarks', {
                  ns: 'components',
                  query: searchQuery,
                })}
              </Typography>
            ) : (
              filteredBenchmarks.map(([group, benchmarkOptions]) => (
                <List
                  subheader={
                    <ListSubheader
                      className="!p-0"
                      data-testid={`benchmark-list-header-${group}`}
                    >
                      <Typography variant="h5">{group}</Typography>
                    </ListSubheader>
                  }
                  dense
                  sx={{ pt: 0 }}
                  key={group}
                  data-testid={`benchmark-list-${group}`}
                >
                  {benchmarkOptions.map((item, index) => (
                    <ListItemButton
                      key={`${group}-${item.value}-${index}`}
                      selected={selectedBenchmark === item.value}
                      onClick={() => onSelectBenchmark(item.value)}
                      className="[&.Mui-selected]:bg-primary/20 [&.Mui-selected]:text-primary [&.Mui-selected]:hover:bg-primary/30"
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                      }}
                      data-testid={`benchmark-list-item-${item.value}`}
                    >
                      <ListItemText
                        primary={item.label}
                        secondary={item.ticker}
                        primaryTypographyProps={{
                          variant: 'body2',
                          fontWeight: selectedBenchmark === item.value ? 500 : 400,
                        }}
                        secondaryTypographyProps={{
                          variant: 'caption',
                        }}
                        data-testid={`benchmark-list-item-label-${item.value}`}
                      />
                    </ListItemButton>
                  ))}
                </List>
              ))
            )}
          </div>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }} className="!gap-8">
          <Button
            onClick={onClose}
            variant="outlined"
            color="secondary"
            className="!flex-1"
            data-testid="close-button"
          >
            {t('common.close', { ns: 'common' })}
          </Button>
          <Button
            onClick={onSave}
            color="secondary"
            className="!flex-1"
            disabled={!selectedBenchmark || benchmarksQuery.isLoading}
            data-testid="save-button"
          >
            {t('common.next', { ns: 'common' })}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
